import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AnalyticsEventName } from '@/analytics/analytics.events';
import { track } from '@/analytics/track';
import { ACCOUNT_CONFIG_QUERY_KEY } from '@/api/accountConfig';
import { ApiError } from '@/api/apiFetch';
import { useAuth } from '@/contexts/AuthContext';
import { doNothing } from '@/utils/doNothing';
import { useMustReadsEnabled } from './hooks/useMustReadsEnabled';
import {
  confirmKhovaNewsfeedPost,
  fetchPendingConfirmations,
  fetchPostConfirmations,
  remindPostConfirmations,
} from './Newsfeed.transport';
import type { NewsfeedItem } from './Newsfeed.types';
import { PENDING_CONFIRMATIONS_QUERY_KEY, POST_CONFIRMATIONS_QUERY_KEY } from './MustReads.keys';

export type MustReadSurface = 'board' | 'home' | 'detail';

const NO_PENDING_POSTS: NewsfeedItem[] = [];

export const isReminderTooSoonError = (error: unknown): boolean => error instanceof ApiError && error.status === 429;

const STALE_CONFIRMATION_EXCLUDED_STATUSES = [401, 429];

/** The post went away or no longer needs the viewer's confirmation (404, 400, 403): retrying cannot help. */
export const isStaleConfirmationError = (error: unknown): boolean =>
  error instanceof ApiError &&
  error.status >= 400 &&
  error.status < 500 &&
  !STALE_CONFIRMATION_EXCLUDED_STATUSES.includes(error.status);

const isFeatureDisabledError = (error: unknown): boolean =>
  error instanceof ApiError && error.code === 'feature-disabled';

/**
 * Must-reads the viewer still has to confirm. Empty while `mustReads` is off, also when a cached list
 * survives a switch flip.
 */
export const usePendingConfirmations = ({ enabled = true }: { enabled?: boolean } = {}): NewsfeedItem[] => {
  const { user } = useAuth();
  const isMustReadsEnabled = useMustReadsEnabled();
  const locale = user?.languageSlug ?? null;
  const isActive = enabled && isMustReadsEnabled && Boolean(user);
  const { data } = useQuery({
    queryKey: [...PENDING_CONFIRMATIONS_QUERY_KEY, locale],
    queryFn: () => fetchPendingConfirmations(locale),
    enabled: isActive,
  });

  if (!isActive) {
    return NO_PENDING_POSTS;
  }
  return data ?? NO_PENDING_POSTS;
};

interface ConfirmPostVariables {
  postId: string;
  surface: MustReadSurface;
}

/**
 * The viewer confirms a must-read (the API also marks it read). The pending list and the post are
 * awaited, so the button stays busy until the card leaves "To confirm" or the post shows it confirmed.
 */
export const useConfirmPostMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ postId }: ConfirmPostVariables) => confirmKhovaNewsfeedPost(postId),
    onSuccess: (_data, { postId, surface }) => {
      track(AnalyticsEventName.NEWSFEED_POST_CONFIRMED, { post_id: postId, surface });
      queryClient.invalidateQueries({ queryKey: ['newsfeed'] }).catch(doNothing);
      queryClient.invalidateQueries({ queryKey: ['newsfeed-unread-count'] }).catch(doNothing);
      queryClient.invalidateQueries({ queryKey: [...POST_CONFIRMATIONS_QUERY_KEY, postId] }).catch(doNothing);
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: PENDING_CONFIRMATIONS_QUERY_KEY }),
        queryClient.invalidateQueries({ queryKey: ['newsfeed-item', postId] }),
      ]);
    },
    onError: (error, { postId }) => {
      if (!isStaleConfirmationError(error)) {
        return;
      }
      queryClient.invalidateQueries({ queryKey: PENDING_CONFIRMATIONS_QUERY_KEY }).catch(doNothing);
      queryClient.invalidateQueries({ queryKey: ['newsfeed-item', postId] }).catch(doNothing);
      if (isFeatureDisabledError(error)) {
        queryClient.invalidateQueries({ queryKey: ACCOUNT_CONFIG_QUERY_KEY }).catch(doNothing);
      }
    },
  });
};

export const usePostConfirmationsQuery = (postId: string, storeId: string | null, enabled: boolean) =>
  useQuery({
    queryKey: [...POST_CONFIRMATIONS_QUERY_KEY, postId, storeId],
    queryFn: () => fetchPostConfirmations(postId, storeId),
    enabled,
    // Keeps the store selector on screen while another store loads; the people list waits for it.
    placeholderData: keepPreviousData,
  });

interface RemindConfirmationsVariables {
  postId: string;
  storeId: string | null;
}

export const useRemindConfirmationsMutation = () => {
  const queryClient = useQueryClient();
  const refetchConfirmations = (postId: string) =>
    queryClient.invalidateQueries({ queryKey: [...POST_CONFIRMATIONS_QUERY_KEY, postId] }).catch(doNothing);

  return useMutation({
    mutationFn: ({ postId, storeId }: RemindConfirmationsVariables) => remindPostConfirmations(postId, storeId),
    onSuccess: ({ reminded }, { postId }) => {
      if (reminded > 0) {
        track(AnalyticsEventName.NEWSFEED_CONFIRMATION_REMINDER_SENT, { post_id: postId, count: reminded });
      }
      refetchConfirmations(postId);
    },
    onError: (error, { postId }) => {
      if (isReminderTooSoonError(error)) {
        refetchConfirmations(postId);
      }
    },
  });
};
